<template>
  <div
    class="clipboard-item-card group"
    :class="{
      'clipboard-item-card--clickable': !inlineEditing && (isNote || insertOnly),
      'clipboard-item-card--editing': inlineEditing,
    }"
    @click="onCardClick"
  >
    <template v-if="inlineEditing">
      <div class="clipboard-item-card__head">
        <ClipboardTypeIcon
          :type="item.type"
          class="clipboard-item-card__type-icon"
        />
        <span class="clipboard-item-card__type">{{ typeLabel }}</span>
        <span
          class="clipboard-item-card__sep"
          aria-hidden="true"
        >·</span>
        <input
          v-model="editTitle"
          type="text"
          class="clipboard-item-card__title-input"
          :placeholder="titlePlaceholder"
          @click.stop
          @keydown.enter.prevent="onSaveEdit"
        >
      </div>
      <textarea
        v-model="editContent"
        class="clipboard-item-card__content-input"
        :class="{ 'clipboard-item-card__content-input--mono': monoBody }"
        rows="4"
        @click.stop
        @keydown.enter.exact.prevent="onSaveEdit"
      />
      <div
        class="clipboard-item-card__edit-actions"
        @click.stop
      >
        <button
          type="button"
          class="clipboard-item-card__edit-btn clipboard-item-card__edit-btn--ghost"
          :disabled="saving"
          @mousedown.stop
          @click.stop="$emit('cancel-edit')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
          Cancel
        </button>
        <button
          type="button"
          class="clipboard-item-card__edit-btn clipboard-item-card__edit-btn--primary"
          :disabled="!canSaveEdit || saving"
          @mousedown.stop
          @click.stop="onSaveEdit"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
          Save
        </button>
      </div>
    </template>

    <template v-else>
      <div class="clipboard-item-card__head">
        <ClipboardTypeIcon
          :type="item.type"
          class="clipboard-item-card__type-icon"
        />
        <span class="clipboard-item-card__type">{{ typeLabel }}</span>
        <template v-if="showTitle">
          <span
            class="clipboard-item-card__sep"
            aria-hidden="true"
          >·</span>
          <span class="clipboard-item-card__title">{{ item.title }}</span>
        </template>
        <div class="clipboard-item-card__right">
          <span class="clipboard-item-card__date">{{ savedAtLabel }}</span>
          <div
            class="clipboard-item-card__hover-actions"
            @click.stop
          >
            <button
              v-if="canInlineEdit"
              type="button"
              class="clipboard-item-card__edit-icon"
              aria-label="Edit"
              title="Edit"
              @click.stop="$emit('edit', item)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                <path d="m15 5 4 4" />
              </svg>
            </button>
            <div class="clipboard-item-card__actions">
              <a
                v-if="amazonUrl"
                :href="amazonUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="clipboard-item-card__action"
                title="Open on Amazon"
                @click.stop
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M15 3h6v6" />
                  <path d="M10 14 21 3" />
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                </svg>
              </a>
              <button
                v-if="insertOnly"
                type="button"
                class="clipboard-item-card__action"
                title="Insert into chat"
                @click.stop="$emit('insert', item)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                  <path d="m21.854 2.147-10.94 10.939" />
                </svg>
              </button>
              <button
                type="button"
                class="clipboard-item-card__action"
                title="Copy"
                @click.stop="$emit('copy', item)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <rect
                    width="14"
                    height="14"
                    x="8"
                    y="8"
                    rx="2"
                    ry="2"
                  />
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>
              </button>
              <button
                type="button"
                class="clipboard-item-card__action clipboard-item-card__action--danger"
                title="Delete"
                :disabled="deleting"
                @click.stop="$emit('delete', item)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 6h18" />
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                  <line
                    x1="10"
                    x2="10"
                    y1="11"
                    y2="17"
                  />
                  <line
                    x1="14"
                    x2="14"
                    y1="11"
                    y2="17"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      <p
        v-if="isNote"
        class="clipboard-item-card__content clipboard-item-card__content--note"
        :class="{ 'clipboard-item-card__content--expanded': expanded }"
        v-html="noteHtml"
      />
      <p
        v-else
        class="clipboard-item-card__content"
        :class="{ 'clipboard-item-card__content--mono': monoBody }"
      >
        {{ displayContent }}
      </p>
    </template>
  </div>
</template>

<script>
import DOMPurify from 'dompurify';
import ClipboardTypeIcon from './ClipboardTypeIcon.vue';
import {
  CLIPBOARD_COMPOSER_CONFIG,
  CLIPBOARD_INLINE_EDITABLE_TYPES,
  CLIPBOARD_TYPE_META,
} from './clipboard-constants';
import {
  extractAsin,
  formatClipboardSavedAt,
  getAmazonUrl,
  getClipboardItemTimestamp,
  getDisplayContent,
  hasMeaningfulTitle,
  linkifyText,
  usesMonoBody,
} from './clipboard-formatters';

export default {
  name: 'ClipboardItemCard',

  components: {
    ClipboardTypeIcon,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },
    insertOnly: {
      type: Boolean,
      default: false,
    },
    deleting: {
      type: Boolean,
      default: false,
    },
    inlineEditing: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      editTitle: '',
      editContent: '',
    };
  },

  computed: {
    isNote() {
      return this.item.type === 'note';
    },

    noteHtml() {
      return DOMPurify.sanitize(linkifyText(this.item.content || ''), {
        ALLOWED_TAGS: ['a'],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
      });
    },

    typeLabel() {
      return (CLIPBOARD_TYPE_META[this.item.type]?.label || this.item.type || '').toUpperCase();
    },

    // Hide the bold title when it's just an auto-derived copy of the content (no user
    // label) so the card doesn't show the same text twice. (POR-13563-2)
    showTitle() {
      return hasMeaningfulTitle(this.item);
    },

    displayContent() {
      return getDisplayContent(this.item);
    },

    monoBody() {
      return usesMonoBody(this.item.type);
    },

    amazonUrl() {
      if (this.item.type !== 'asin') {
        return null;
      }
      return getAmazonUrl(extractAsin(this.item.content));
    },

    savedAtLabel() {
      return formatClipboardSavedAt(getClipboardItemTimestamp(this.item));
    },

    canInlineEdit() {
      return CLIPBOARD_INLINE_EDITABLE_TYPES.has(this.item.type);
    },

    titlePlaceholder() {
      return CLIPBOARD_COMPOSER_CONFIG[this.item.type]?.labelPlaceholder || 'Label';
    },

    canSaveEdit() {
      return Boolean(String(this.editContent || '').trim());
    },
  },

  watch: {
    inlineEditing: {
      immediate: true,
      handler(editing) {
        if (editing) {
          this.editTitle = this.item.title || '';
          this.editContent = this.item.content || '';
          this.$nextTick(() => {
            const input = this.$el?.querySelector('.clipboard-item-card__title-input');
            input?.focus?.();
            input?.select?.();
          });
        }
      },
    },
  },

  methods: {
    onCardClick() {
      if (this.inlineEditing) {
        return;
      }
      if (this.isNote) {
        this.$emit('toggle-expand', this.item);
        return;
      }
      if (this.insertOnly) {
        this.$emit('insert', this.item);
      }
    },

    onSaveEdit() {
      if (!this.canSaveEdit || this.saving) {
        return;
      }
      this.$emit('save-edit', {
        item: this.item,
        title: String(this.editTitle || '').trim(),
        content: String(this.editContent || '').trim(),
      });
    },
  },
};
</script>

<style scoped>
.clipboard-item-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
  transition: border-color 140ms ease;
}

.clipboard-item-card--clickable {
  cursor: pointer;
}

.clipboard-item-card:hover {
  border-color: rgba(17, 24, 39, 0.2);
}

.clipboard-item-card--editing:hover {
  border-color: #e5e7eb;
}

.clipboard-item-card__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.75rem;
  line-height: 1;
  min-width: 0;
}

.clipboard-item-card__type-icon {
  width: 14px;
  height: 14px;
}

.clipboard-item-card__type {
  flex-shrink: 0;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
}

.clipboard-item-card__sep {
  color: #9ca3af;
}

.clipboard-item-card__title {
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1 1 auto;
  max-width: 100%;
}

.clipboard-item-card__right {
  margin-left: auto;
  position: relative;
  flex: 0 0 132px;
  height: 20px;
}

.clipboard-item-card__date {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  padding-right: 2px;
  opacity: 1;
  transition: opacity 140ms ease;
}

.clipboard-item-card__edit-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6b7280;
  transition: background 140ms ease, color 140ms ease;
}

.clipboard-item-card__edit-icon:hover {
  background: #f3f4f6;
  color: #111827;
}

.clipboard-item-card__actions {
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  display: flex;
}

.clipboard-item-card__hover-actions {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 140ms ease;
}

.group:hover .clipboard-item-card__hover-actions,
.group:focus-within .clipboard-item-card__hover-actions {
  opacity: 1;
  pointer-events: auto;
}

.group:hover .clipboard-item-card__date,
.group:focus-within .clipboard-item-card__date {
  opacity: 0;
}

.clipboard-item-card__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6b7280;
  line-height: 1;
  text-decoration: none;
  transition: color 120ms ease;
}

.clipboard-item-card__action:hover:not(:disabled) {
  color: #111827;
}

.clipboard-item-card__action--danger:hover:not(:disabled) {
  color: #dc2626;
}

.clipboard-item-card__action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.clipboard-item-card__content {
  margin: 6px 0 0;
  font-size: 0.875rem;
  line-height: 1.375;
  color: rgba(17, 24, 39, 0.8);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.clipboard-item-card__content--note {
  white-space: pre-wrap;
  word-break: break-word;
}

.clipboard-item-card__content--expanded {
  display: block;
  -webkit-line-clamp: unset;
  overflow: visible;
}


.clipboard-item-card__content--note :deep(.clipboard-note-link) {
  color: #059669;
  text-decoration: underline;
  word-break: break-all;
}

.clipboard-item-card__content--note :deep(.clipboard-note-link:hover) {
  color: #047857;
}

.clipboard-item-card__content--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  letter-spacing: 0.05em;
  color: #111827;
}

.clipboard-item-card__title-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 24px;
  padding: 0 6px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #111827;
  background: #fff;
}

.clipboard-item-card__title-input:focus {
  outline: none;
  border-color: #111827;
  box-shadow: 0 0 0 1px #111827;
}

.clipboard-item-card__content-input {
  display: block;
  width: 100%;
  margin-top: 8px;
  min-height: 80px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  line-height: 1.375;
  color: #111827;
  resize: vertical;
}

.clipboard-item-card__content-input:focus {
  outline: none;
  border-color: #111827;
  box-shadow: 0 0 0 1px #111827;
}

.clipboard-item-card__content-input--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  letter-spacing: 0.05em;
}

.clipboard-item-card__edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;
}

.clipboard-item-card__edit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
  transition: background 140ms ease, color 140ms ease;
}

.clipboard-item-card__edit-btn--ghost {
  background: transparent;
  color: #111827;
}

.clipboard-item-card__edit-btn--ghost:hover:not(:disabled) {
  background: #f3f4f6;
}

.clipboard-item-card__edit-btn--primary {
  background: #59ba9e;
  color: #fff;
}

.clipboard-item-card__edit-btn--primary:hover:not(:disabled) {
  background: #4aa88d;
}

.clipboard-item-card__edit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
